import { zodResolver } from '@hookform/resolvers/zod';
import {
    Button,
    EnvelopeIcon,
    ErrorMessage,
    PasswordInput,
    TextInput,
    useGlobalAuth,
} from '@keyliving/component-lib';
import { useCallback, useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import { useTypedSearchParams } from 'react-router-typesafe-routes/dom';
import { object, output, string } from 'zod';

import FullScreenFallbackLayout from '../components/layout/FullScreenFallbackLayout';
import { useAnalytics, useAppDispatch } from '../hooks';
import {
    setAuthDetails,
    setCurrentUser,
    useLazyMeQuery,
    useLazyVerifyAuthTokenQuery,
    useSigninMutation,
} from '../redux/modules/auth';
import { URLS } from '../routes/urls';
import classes from './Auth.module.scss';

const validation = object({
    email: string()
        .min(1, { message: 'Email is required' })
        .email({ message: 'Invalid email address' }),
    password: string().min(1, { message: 'Password is required' }),
});

type Inputs = output<typeof validation>;

export default function Login() {
    const [pageLoading, setPageLoading] = useState<boolean>(true);
    const [{ email, redirectUrl, token }, setSearchParams] = useTypedSearchParams(URLS.Login);
    const [signIn, { isError, isLoading: signInLoading }] = useSigninMutation();
    const [verifyAuthToken] = useLazyVerifyAuthTokenQuery();
    const { trackEvent } = useAnalytics();
    const { setToken: setAuthToken } = useGlobalAuth();
    const dispatch = useAppDispatch();
    const [getCurrentUserDetails] = useLazyMeQuery();
    const navigate = useNavigate();

    const {
        formState: { errors: validationErrors },
        handleSubmit,
        register,
    } = useForm<Inputs>({
        resolver: zodResolver(validation),
        defaultValues: {
            email,
            password: '',
        },
    });

    const onSubmit: SubmitHandler<Inputs> = async ({ email, password }) => {
        const resp = await signIn({ email, password });

        if ('data' in resp) {
            const { claims, token } = resp.data;

            trackEvent('login', {
                redirectUrl,
            });

            setAuthToken(token, {
                expires: new Date(claims.exp * 1_000), // num milliseconds
            });

            // Redirect back to the original client/page
            window.location.href = redirectUrl;
        }
    };

    const verifyAndSetAuthToken = useCallback(
        async (authToken: string): Promise<void> => {
            try {
                // Verify the token - save claims, and token
                const claims = await verifyAuthToken(authToken).unwrap();
                dispatch(setAuthDetails({ claims, token: authToken, user: null }));

                // Token expiry should be handled by the api

                // Save the token to the auth cookie
                setAuthToken(authToken, {
                    expires: new Date(claims.exp * 1_000), // num milliseconds
                });

                return Promise.resolve();
            } catch (error) {
                return Promise.reject('Token has expired');
            }
        },
        [verifyAuthToken, dispatch, setAuthToken]
    );

    const handleMagicLogin = useCallback(
        async (authToken: string) => {
            try {
                // Verify the token - save claims, and token
                await verifyAndSetAuthToken(authToken);

                // Get the user - save user details
                const user = await getCurrentUserDetails().unwrap();
                dispatch(setCurrentUser(user));

                trackEvent('magic_login', {
                    redirectUrl,
                });

                // Redirect back to the original client/page
                window.location.href = redirectUrl;
            } catch (error) {
                /**
                 * Just swallow the error for now and show the login form.
                 */
                setSearchParams((params) => ({
                    ...params,
                    token: undefined,
                }));
                setPageLoading(false);
            }
        },
        [
            dispatch,
            getCurrentUserDetails,
            redirectUrl,
            setSearchParams,
            trackEvent,
            verifyAndSetAuthToken,
        ]
    );

    useEffect(() => {
        if (token) {
            handleMagicLogin(token);
        } else {
            setPageLoading(false);
        }

        // once on load
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (pageLoading) {
        return <FullScreenFallbackLayout isLoading />;
    }

    return (
        <>
            <div className="content">
                <h1 className="h3">Welcome back!</h1>
            </div>

            <form className={classes.form} onSubmit={handleSubmit(onSubmit)}>
                <TextInput
                    autoCapitalize="none"
                    autoCorrect="off"
                    errors={validationErrors}
                    inputMode="email"
                    label="Email"
                    placeholder="Email Address"
                    {...register('email')}
                />
                <PasswordInput
                    errors={validationErrors}
                    label="Password"
                    placeholder="Password"
                    {...register('password')}
                />

                <div className={classes.submit}>
                    <Button
                        isLoading={signInLoading}
                        size="md"
                        theme="tertiary"
                        type="submit"
                        width="full"
                    >
                        Login
                    </Button>
                </div>
            </form>

            <Button
                className={classes['btn--magic-link']}
                fill="outline"
                icon={<EnvelopeIcon />}
                onClick={(e) => {
                    e.stopPropagation();
                    navigate(URLS.MagicLink.buildPath({}, { email, redirectUrl }));
                }}
                size="md"
                theme="tertiary"
                type="button"
                width="full"
            >
                Login with a magic link
            </Button>

            {isError && (
                <div className={classes.error}>
                    <ErrorMessage message="Email or Password is incorrect" name="form-error" />
                </div>
            )}

            <div className={classes['form-switcher']}>
                <Link
                    className={classes.link}
                    to={URLS.ForgotPassword.buildPath({}, { email, redirectUrl })}
                >
                    Forgot password?
                </Link>
            </div>
        </>
    );
}
